/**
 * The action bars — tasks C7 and C8, ARCHITECTURE §05.4 and §05.5.
 *
 * Both bars read `actionRegistry`, so "which actions exist", "which are
 * destructive" and "which statuses each applies to" are written down once.
 *
 * **Absent, not disabled** (criterion 15). An action the user lacks permission
 * for is not rendered at all: a disabled "Disconnect" still advertises that
 * disconnection exists and that somebody can do it. And, as everywhere: this is
 * presentation. The API refuses the same request regardless.
 */

import type { PlanSummary, SimSummary } from '@rd/shared';
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../components/Button.js';
import { useToast } from '../../components/Toast.js';
import { useJobDrawer } from '../../lib/JobDrawerContext.js';
import { useSelection } from '../inventory/selection.js';

import { ActionDialog } from './actions/ActionDialog.js';
import {
  actionsFor,
  bulkActionsFor,
  SIM_ACTIONS,
  type SimActionDef,
} from './actions/actionRegistry.js';
import { DisconnectDialog, PlanActionDialog, SwapDialog } from './actions/SimActionDialogs.js';
import { useSimAction } from './actions/useSimAction.js';
import { refsNeedingReveal, useRevealedSims } from './useRevealedSims.js';

/** Renders whichever dialog shape the chosen action needs. */
function ActionRunner({
  action,
  targets,
  excluded,
  plans,
  onDone,
  onCancel,
}: {
  action: SimActionDef;
  targets: SimSummary[];
  excluded: SimSummary[];
  plans: PlanSummary[];
  onDone(): void;
  onCancel(): void;
}): ReactElement | null {
  const { submit, isSubmitting, error } = useSimAction(action);
  const { track } = useJobDrawer();
  const toast = useToast();

  // PR-8: rows arrive masked (§B4), but the lifecycle POST bodies require the
  // real values — `msisdnSchema` 422s a masked string. The reveal fetch starts
  // when the dialog opens (overlapping the operator reading it) and the submit
  // awaits the same promise, so a fast confirm cannot post masked values.
  const { ensureRevealed } = useRevealedSims(refsNeedingReveal(targets));

  const run = async (extra: Record<string, unknown>): Promise<void> => {
    try {
      const revealed = await ensureRevealed();
      const identifiers = (sim: SimSummary): { msisdn: string | null; iccid: string | null } =>
        revealed.get(sim.simRef) ?? { msisdn: sim.msisdn, iccid: sim.iccid };
      // Swap's `existingIccid` came off the dialog's (masked) row; the revealed
      // value replaces it so the pair posted is the real one.
      const existingIccid =
        action.key === 'swap' && targets[0]
          ? (identifiers(targets[0]).iccid ?? extra.existingIccid)
          : extra.existingIccid;
      const job = await submit({
        items: targets.map((sim) => {
          const ids = identifiers(sim);
          return {
            msisdn: ids.msisdn ?? '',
            ...(ids.iccid ? { iccid: ids.iccid } : {}),
            ...(extra.planCode ? { planCode: String(extra.planCode) } : {}),
          };
        }),
        ...(extra.reason ? { reason: String(extra.reason) } : {}),
        ...(extra.consent === undefined ? {} : { consent: Boolean(extra.consent) }),
        ...(existingIccid ? { existingIccid: String(existingIccid) } : {}),
        ...(extra.newIccid ? { newIccid: String(extra.newIccid) } : {}),
      });
      // §05.8: no success toast — nothing has succeeded yet. The drawer is the
      // only report until the job reaches a terminal state.
      track(job.ref);
      onDone();
    } catch {
      // `error` is rendered inside the dialog, which stays open so the operator
      // can correct and retry with the same idempotency key.
      toast.push('error', `${action.label} could not be submitted.`);
    }
  };

  if (action.key === 'swap') {
    const sim = targets[0];
    if (!sim) return null;
    return (
      <SwapDialog
        open
        sim={sim}
        targets={targets}
        busy={isSubmitting}
        error={error}
        onCancel={onCancel}
        onConfirm={(input) => void run({ ...input })}
      />
    );
  }

  if (action.key === 'disconnect') {
    return (
      <DisconnectDialog
        open
        targets={targets}
        excluded={excluded}
        busy={isSubmitting}
        error={error}
        onCancel={onCancel}
        onConfirm={(input) => void run({ ...input })}
      />
    );
  }

  if (action.requiresPlan) {
    return (
      <PlanActionDialog
        open
        action={action}
        plans={plans}
        targets={targets}
        excluded={excluded}
        busy={isSubmitting}
        error={error}
        onCancel={onCancel}
        onConfirm={(input) => void run({ ...input })}
      />
    );
  }

  return (
    <ActionDialog
      action={action}
      open
      targets={targets}
      excluded={excluded}
      busy={isSubmitting}
      error={error}
      onCancel={onCancel}
      onConfirm={() => void run({})}
    />
  );
}

/** SIM-detail bar: the actions available for one SIM. */
export function ActionBar({
  sim,
  plans,
  onSubmitted,
}: {
  sim: SimSummary;
  plans: PlanSummary[];
  onSubmitted?(): void;
}): ReactElement {
  const auth = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState<SimActionDef | null>(null);
  const available = actionsFor(sim, auth);

  return (
    <div className="flex flex-wrap gap-2" aria-label="SIM actions">
      {available.map((action) =>
        // Swap and activate each have their own screen rather than a dialog.
        // Swap needs the whole width to show which SIM loses service and which
        // gains it. Activate is billable, and this file's own rule for the bulk
        // bar is that the plan, price and count stated in full ARE the
        // safeguard, in place of a typed phrase (§06c) — a dialog opened from
        // one row was the one place that rule did not hold.
        action.key === 'swap' || action.key === 'activate' ? (
          <Button
            key={action.key}
            variant={action.key === 'swap' ? 'danger' : 'action'}
            onClick={() => void navigate(`/sims/${sim.simRef}/${action.key}`)}
          >
            {action.label}
          </Button>
        ) : (
          <Button
            key={action.key}
            variant={action.destructive ? 'danger' : 'secondary'}
            onClick={() => setActive(action)}
          >
            {action.label}
          </Button>
        ),
      )}

      {available.length === 0 ? (
        <p className="text-sm text-muted">No actions are available for this SIM.</p>
      ) : null}

      {active ? (
        <ActionRunner
          action={active}
          targets={[sim]}
          excluded={[]}
          plans={plans}
          onCancel={() => setActive(null)}
          onDone={() => {
            setActive(null);
            onSubmitted?.();
          }}
        />
      ) : null}
    </div>
  );
}

/** Estate-list bar: the selection, the cap, and per-action eligibility. */
export function BulkActionBar({ plans }: { plans: PlanSummary[] }): ReactElement | null {
  const auth = useAuth();
  const selection = useSelection();
  const navigate = useNavigate();
  const [active, setActive] = useState<SimActionDef | null>(null);

  const sims = selection.selectedSims();
  if (sims.length === 0) return null;

  const offers = bulkActionsFor(sims, auth);
  const activeOffer = active ? offers.find((o) => o.action.key === active.key) : undefined;

  return (
    <div
      className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-line bg-surface p-3"
      aria-label="Bulk actions"
    >
      <p className="text-sm font-medium text-body" data-testid="selection-count">
        {selection.count} selected
        {selection.atCap ? ` — maximum ${selection.cap} per action` : ''}
      </p>

      <Button variant="ghost" onClick={selection.clear}>
        Clear
      </Button>

      <div className="flex flex-wrap gap-2">
        {/*
          Activation has its own screen rather than a dialog. It is the daily
          primary action and it is billable, so the operator gets a page that
          states plan, price and count in full — the information IS the
          safeguard, in place of a typed phrase (§06c). Everything else still
          goes through the shared confirmation dialog.
        */}
        {offers.some((offer) => offer.action.key === 'activate') ? (
          <Button onClick={() => void navigate('/sims/activate')}>
            Activate
            {(() => {
              const offer = offers.find((o) => o.action.key === 'activate');
              return offer && offer.ineligible.length > 0
                ? ` (${offer.eligible.length} of ${sims.length} eligible)`
                : '';
            })()}
          </Button>
        ) : null}

        {offers
          .filter((offer) => offer.action.key !== 'activate')
          .map((offer) => (
            <Button
              key={offer.action.key}
              variant={offer.action.destructive ? 'danger' : 'secondary'}
              onClick={() => setActive(offer.action)}
            >
              {/* Mixed eligibility is disclosed on the button itself, so the
                operator learns it before opening anything. */}
              {offer.ineligible.length === 0
                ? offer.action.label
                : `${offer.action.label} (${offer.eligible.length} of ${sims.length} eligible)`}
            </Button>
          ))}
      </div>

      {selection.capMessage ? (
        <p role="status" data-testid="cap-message" className="w-full text-sm text-warning">
          {selection.capMessage}
        </p>
      ) : null}

      {active && activeOffer ? (
        <ActionRunner
          action={active}
          targets={activeOffer.eligible}
          excluded={activeOffer.ineligible}
          plans={plans}
          onCancel={() => setActive(null)}
          onDone={() => {
            setActive(null);
            selection.clear();
          }}
        />
      ) : null}
    </div>
  );
}

export { SIM_ACTIONS };
